import { Phone, Mail, MapPin, Clock3 } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { BUSINESS, SERVICE_AREAS } from "../lib/content";

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Get In Touch"
        title="Let's talk about"
        accent="your project."
        subtitle="Call, email or reach us through any of our regional offices. No pressure — just honest answers and a clear, written quote."
        testId="contact-hero"
      />

      {/* Main grid */}
      <section className="py-24 md:py-32" data-testid="contact-main">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-10">
            <div>
              <div className="label-sm text-[#f59e0b] mb-5">Call us direct</div>
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="group inline-flex flex-col"
                data-testid="contact-phone"
              >
                <div className="font-display font-black text-5xl sm:text-7xl md:text-[8rem] uppercase leading-none tracking-tight group-hover:text-[#f59e0b] transition-colors">
                  {BUSINESS.phone}
                </div>
                <div className="flex items-center gap-3 mt-5 text-xs uppercase tracking-[0.3em] text-[#a1a1aa]">
                  <Phone className="w-4 h-4 text-[#f59e0b]" strokeWidth={3} />
                  Tap to call · Mon–Sat
                </div>
              </a>
            </div>

            <div className="border-t border-[#27272a] pt-10">
              <div className="label-sm text-[#f59e0b] mb-5">Email us</div>
              <div className="space-y-4">
                <a
                  href={`mailto:${BUSINESS.email1}`}
                  className="group flex items-center gap-5 border border-[#27272a] hover:border-[#f59e0b] p-5 md:p-6 transition-colors"
                  data-testid="contact-email-1"
                >
                  <div className="w-12 h-12 bg-[#18181b] border border-[#27272a] group-hover:border-[#f59e0b] flex items-center justify-center shrink-0 transition-colors">
                    <Mail className="w-5 h-5 text-[#f59e0b]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-[#a1a1aa] mb-1">
                      General inquiries
                    </div>
                    <div className="font-display font-bold text-2xl md:text-3xl group-hover:text-[#f59e0b] transition-colors break-all">
                      {BUSINESS.email1}
                    </div>
                  </div>
                </a>
                <a
                  href={`mailto:${BUSINESS.email2}`}
                  className="group flex items-center gap-5 border border-[#27272a] hover:border-[#f59e0b] p-5 md:p-6 transition-colors"
                  data-testid="contact-email-2"
                >
                  <div className="w-12 h-12 bg-[#18181b] border border-[#27272a] group-hover:border-[#f59e0b] flex items-center justify-center shrink-0 transition-colors">
                    <Mail className="w-5 h-5 text-[#f59e0b]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-[#a1a1aa] mb-1">
                      Quotes & projects
                    </div>
                    <div className="font-display font-bold text-2xl md:text-3xl group-hover:text-[#f59e0b] transition-colors break-all">
                      {BUSINESS.email2}
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Info sidebar */}
          <div className="lg:col-span-5 space-y-5">
            <div
              className="bg-[#18181b] border border-[#27272a] p-8 md:p-10"
              data-testid="contact-hours"
            >
              <div className="flex items-center gap-3 mb-5">
                <Clock3 className="w-5 h-5 text-[#f59e0b]" />
                <div className="label-sm text-[#f59e0b]">Hours</div>
              </div>
              <ul className="space-y-3 text-[#fafaf9]">
                <li className="flex items-center justify-between border-b border-[#27272a] pb-3">
                  <span>Monday – Friday</span>
                  <span className="font-bold">7:30 AM – 5:30 PM</span>
                </li>
                <li className="flex items-center justify-between border-b border-[#27272a] pb-3">
                  <span>Saturday</span>
                  <span className="font-bold">By appointment</span>
                </li>
                <li className="flex items-center justify-between">
                  <span>Sunday</span>
                  <span className="text-[#a1a1aa]">Closed</span>
                </li>
              </ul>
            </div>

            <div
              className="bg-[#18181b] border border-[#27272a] p-8 md:p-10"
              data-testid="contact-areas"
            >
              <div className="flex items-center gap-3 mb-5">
                <MapPin className="w-5 h-5 text-[#f59e0b]" />
                <div className="label-sm text-[#f59e0b]">Regional offices</div>
              </div>
              <ul className="space-y-4">
                {SERVICE_AREAS.map((a, i) => (
                  <li
                    key={i}
                    className="flex items-start justify-between gap-4 pb-4 border-b border-[#27272a] last:border-0 last:pb-0"
                  >
                    <div>
                      <div className="font-display font-bold text-2xl uppercase">
                        {a.city}
                      </div>
                      <div className="text-xs uppercase tracking-[0.25em] text-[#a1a1aa] mt-1">
                        {a.region}
                      </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#f59e0b] shrink-0 mt-2">
                      Serving
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="bg-[#f59e0b] text-[#09090b] p-8 md:p-10"
              data-testid="contact-cta-card"
            >
              <div className="text-xs uppercase tracking-[0.3em] font-bold mb-4">
                Fast response
              </div>
              <div className="font-display font-black text-3xl md:text-4xl uppercase leading-tight mb-6">
                Prefer to talk?
              </div>
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="inline-flex items-center gap-3 bg-[#09090b] hover:bg-[#18181b] text-[#fafaf9] font-bold px-6 py-4 text-xs uppercase tracking-[0.25em] transition-colors"
                data-testid="contact-cta-call"
              >
                <Phone className="w-4 h-4" strokeWidth={3} />
                {BUSINESS.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Promise band */}
      <section
        className="py-20 md:py-24 bg-[#050505] border-y border-[#27272a]"
        data-testid="contact-promise"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 text-center">
          <div className="label-sm text-[#f59e0b] mb-6">Our promise</div>
          <div className="font-display font-black text-3xl md:text-5xl uppercase leading-[0.95] max-w-4xl mx-auto">
            No pressure. No surprises.
            <br />
            <span className="text-[#f59e0b]">Just honest, durable work.</span>
          </div>
        </div>
      </section>
    </div>
  );
}
